import React, { ComponentProps, useCallback } from 'react';
import { Controller } from 'react-hook-form';

import { PickerValidDate } from '@mui/x-date-pickers/models/pickers';

import {
  Box,
  DateTimePicker,
  TextFieldProps,
  useWindowedTooltipZIndex,
  withTitle,
  withWindowedPopperProps,
} from '@sbv-clients/ui-shared';

import { useFormFieldError } from '../../hooks/use-field-error';

type PickerPropsType = {
  minDate?: PickerValidDate;
  maxDate?: PickerValidDate;
  onOpen?: () => void;
  onClose?: () => void;
};

type TextFieldPropsType = {
  name: string;
  label?: TextFieldProps['label'];
  fullWidth?: TextFieldProps['fullWidth'];
  disabled?: TextFieldProps['disabled'];
  required?: TextFieldProps['required'];
  size: TextFieldProps['size'];
  clearable?: boolean;
};

type FormDateTimePickerFieldProps = PickerPropsType & TextFieldPropsType;
type RenderType = ComponentProps<typeof Controller>['render'];
const FormDateTimePickerFieldWithNotTitle = ({
  name,
  label,
  fullWidth,
  disabled,
  required,
  size,
  minDate,
  maxDate,
  onClose,
  onOpen,
  clearable,
}: FormDateTimePickerFieldProps) => {
  const { error, helperText } = useFormFieldError(name);

  const windowedConfig = useWindowedTooltipZIndex();

  const render: RenderType = useCallback(
    ({ field }) => {
      const slotProps = {
        textField: {
          fullWidth,
          required,
          size,
          helperText,
          error,
        },
        field: { clearable },
        popper: withWindowedPopperProps(windowedConfig, undefined),
      };
      return (
        <Box>
          <DateTimePicker
            disabled={disabled}
            minDate={minDate}
            maxDate={maxDate}
            onOpen={onOpen}
            onClose={onClose}
            {...field}
            slotProps={slotProps}
          />
        </Box>
      );
    },
    [
      fullWidth,
      required,
      size,
      helperText,
      error,
      clearable,
      disabled,
      minDate,
      maxDate,
      onOpen,
      onClose,
      windowedConfig,
    ]
  );

  return <Controller name={name} render={render} />;
};

export const FormDateTimePickerField = withTitle(
  FormDateTimePickerFieldWithNotTitle
);
